import React, { useEffect, useState } from 'react';
import { Card, Form, TimePicker, Button, message, Typography, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useAuthStore } from '../store/authStore';
import { suppliersAPI } from '../services/api';

const { Paragraph, Text } = Typography;

const Settings: React.FC = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<{ cancelTime: Dayjs | null; orderDeadline: Dayjs | null }>();

  useEffect(() => {
    if (user?.role !== 'supplier') return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await suppliersAPI.getMyOrderRules();
        if (cancelled) return;
        const t = data.employeeCancelDeadlineTime;
        const od = data.orderDeadline;
        form.setFieldsValue({
          cancelTime: t && /^\d{1,2}:\d{2}$/.test(t) ? dayjs(`1970-01-01T${t}:00`) : null,
          orderDeadline: od && /^\d{1,2}:\d{2}$/.test(od) ? dayjs(`1970-01-01T${od}:00`) : null
        });
      } catch (e: any) {
        message.error(e?.response?.data?.error || 'Не удалось загрузить настройки');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.role, form]);

  const onSave = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const timeStr = v.cancelTime != null ? v.cancelTime.format('HH:mm') : null;
      const deadlineStr = v.orderDeadline != null ? v.orderDeadline.format('HH:mm') : null;
      await suppliersAPI.updateMyOrderRules({
        employeeCancelDeadlineTime: timeStr,
        orderDeadline: deadlineStr
      });
      message.success('Сохранено');
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (user?.role !== 'supplier') {
    return (
      <Card title="Настройки">
        <p>Для вашей роли отдельных настроек пока нет.</p>
      </Card>
    );
  }

  return (
    <Card title="Настройки поставщика" loading={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 480 }}>
        <div>
          <Text strong>Дедлайн приёма заказов (на сегодня)</Text>
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            До этого времени сотрудники могут оформлять заказ на текущий день (если внеплановый режим не активен).
          </Paragraph>
          <Form form={form} layout="vertical">
            <Form.Item
              name="orderDeadline"
              label="Заказы принимаются до"
              tooltip="Формат 24 ч, локальное время сервера"
            >
              <TimePicker format="HH:mm" minuteStep={5} allowClear style={{ width: '100%' }} />
            </Form.Item>
          </Form>
        </div>

        <div>
          <Text strong>Срок отказа сотрудника заказчика</Text>
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Укажите время (в день доставки), до которого сотрудник может отказаться от участия в заказе,
            в том числе после того как заказчик отправил заказ вам. Если поле пустое — от уже отправленного
            заказа сотрудник отказаться через систему не сможет (как раньше).
          </Paragraph>
          <Form form={form} layout="vertical">
            <Form.Item
              name="cancelTime"
              label="До времени в день доставки"
              tooltip="Формат 24 ч, локальное время сервера"
            >
              <TimePicker format="HH:mm" minuteStep={5} allowClear style={{ width: '100%' }} />
            </Form.Item>
          </Form>
          <Button type="primary" onClick={onSave} loading={saving}>
            Сохранить
          </Button>
        </div>
      </Space>
    </Card>
  );
};

export default Settings;
